import React, { useEffect, useState } from "react";
import {
  MapPin as MapPinIcon,
  Loader2,
  Sun,
  Calendar,
  ArrowRight,
} from "lucide-react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  ZoomControl,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { OFFICES } from "../data/mock";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Centro Italia (default iniziale prima del fit)
const ITALY_CENTER = [42.5, 12.5];
const ITALY_ZOOM = 6;

// Icona pin custom in stile Energeide (giallo + outline scuro)
const energeideIcon = L.divIcon({
  className: "energeide-pin",
  html: `
    <div style="
      position: relative;
      width: 30px;
      height: 40px;
    ">
      <svg viewBox="0 0 30 40" width="30" height="40" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M15 0 C6.7 0 0 6.7 0 15 c0 11 15 25 15 25 s15-14 15-25 c0-8.3-6.7-15-15-15z"
          fill="#F4C542"
          stroke="#0A1F44"
          stroke-width="2"
        />
        <circle cx="15" cy="15" r="6" fill="#0A1F44"/>
        <circle cx="15" cy="15" r="2.5" fill="#F4C542"/>
      </svg>
    </div>
  `,
  iconSize: [30, 40],
  iconAnchor: [15, 40],
  popupAnchor: [0, -36],
});

// Icone dedicate per le sedi Energeide (una per ogni tipo)
const OFFICE_ICON_CONFIG = {
  commerciale: {
    fill: "#0A1F44",
    stroke: "#F4C542",
    // Casa
    inner:
      '<path d="M8 18 L15 12 L22 18 L22 24 L8 24 Z" fill="#0FB36B" stroke="#F4C542" stroke-width="1"/><rect x="13.5" y="19" width="3" height="5" fill="#0A1F44"/>',
  },
  magazzino: {
    fill: "#0FB36B",
    stroke: "#0A1F44",
    // Capannone / magazzino
    inner:
      '<rect x="8" y="13" width="14" height="11" fill="#F4C542" stroke="#0A1F44" stroke-width="1"/><path d="M8 13 L15 9 L22 13 Z" fill="#0A1F44"/><rect x="13.5" y="18" width="3" height="6" fill="#0A1F44"/>',
  },
  legale: {
    fill: "#F4C542",
    stroke: "#0A1F44",
    // Documento
    inner:
      '<rect x="10" y="11" width="10" height="13" fill="#FFFFFF" stroke="#0A1F44" stroke-width="1"/><line x1="12" y1="15" x2="18" y2="15" stroke="#0A1F44" stroke-width="1"/><line x1="12" y1="18" x2="18" y2="18" stroke="#0A1F44" stroke-width="1"/><line x1="12" y1="21" x2="16" y2="21" stroke="#0A1F44" stroke-width="1"/>',
  },
};

const makeOfficeIcon = (kind) => {
  const cfg = OFFICE_ICON_CONFIG[kind] || OFFICE_ICON_CONFIG.commerciale;
  return L.divIcon({
    className: `energeide-pin energeide-pin--${kind}`,
    html: `
      <div style="position: relative; width: 34px; height: 44px;">
        <svg viewBox="0 0 30 40" width="34" height="44" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M15 0 C6.7 0 0 6.7 0 15 c0 11 15 25 15 25 s15-14 15-25 c0-8.3-6.7-15-15-15z"
            fill="${cfg.fill}"
            stroke="${cfg.stroke}"
            stroke-width="2"
          />
          ${cfg.inner}
        </svg>
      </div>
    `,
    iconSize: [34, 44],
    iconAnchor: [17, 44],
    popupAnchor: [0, -40],
  });
};

const officeIcons = OFFICES.reduce((acc, o) => {
  acc[o.id] = makeOfficeIcon(o.kind);
  return acc;
}, {});

const LEGEND_OFFICE_LABEL = {
  commerciale: "Sede commerciale",
  magazzino: "Sede magazzino",
  legale: "Sede legale",
};

const MapLegend = ({ count }) => (
  <div
    data-testid="map-legend"
    className="absolute bottom-3 left-3 z-[400] bg-white/95 backdrop-blur-sm rounded-lg shadow-md px-3 py-2 flex flex-col gap-1.5 text-xs font-montserrat"
  >
    <div className="flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full bg-[#F4C542] border-2 border-[#0A1F44]" />
      <span className="text-[#0A1F44] font-semibold">
        {count} {count === 1 ? "installazione" : "installazioni"}
      </span>
    </div>
    {OFFICES.map((o) => {
      const cfg = OFFICE_ICON_CONFIG[o.kind] || OFFICE_ICON_CONFIG.commerciale;
      return (
        <div key={o.id} className="flex items-center gap-2">
          <span
            className="w-2.5 h-2.5 rounded-full border-2"
            style={{ backgroundColor: cfg.fill, borderColor: cfg.stroke }}
          />
          <span className="text-[#0A1F44] font-semibold">
            {LEGEND_OFFICE_LABEL[o.kind] || o.title}
          </span>
        </div>
      );
    })}
  </div>
);

/**
 * Componente interno che chiama fitBounds quando le locations cambiano.
 * Garantisce che TUTTI i pin siano sempre visibili e separa quelli sovrapposti.
 */
const FitBoundsToMarkers = ({ locations }) => {
  const map = useMap();
  useEffect(() => {
    const points = [
      ...(locations || []).map((l) => [l.lat, l.lng]),
      ...OFFICES.map((o) => [o.lat, o.lng]),
    ];
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 11, { animate: false });
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
  }, [locations, map]);
  return null;
};

const getImagesForLocation = (projects, id) => {
  if (!projects || !id) return [];
  const p = projects.find((x) => x.id === id);
  if (!p) return [];
  if (Array.isArray(p.images) && p.images.length > 0) return p.images;
  if (p.image) return [p.image];
  return [];
};

const ProjectsMap = ({ onSelectProject, projects }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/projects/locations`)
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((json) => {
        if (!cancelled) setLocations(json?.data || []);
      })
      .catch(() => {
        if (!cancelled) setLocations([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="mappa" className="py-12 sm:py-16 bg-gray-50 scroll-mt-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#F4C542]/15 rounded-full px-4 py-1.5 mb-4">
            <MapPinIcon className="w-4 h-4 text-[#F4C542]" />
            <span className="text-xs text-[#F4C542] font-montserrat font-semibold tracking-wider">
              DOVE LAVORIAMO
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl text-[#0A1F44] font-montserrat font-bold mb-3">
            I nostri impianti sulla mappa
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Ogni pin è un'installazione reale. Clicca su un pin per scoprire i
            dettagli del progetto.
          </p>
        </div>

        <div
          data-testid="projects-map-container"
          className="relative isolate w-full rounded-2xl overflow-hidden shadow-lg border border-gray-100 bg-white"
          style={{ height: "520px" }}
        >
          {loading ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 bg-white z-[500]">
              <Loader2 className="w-8 h-8 animate-spin text-[#F4C542] mb-3" />
              <p className="text-sm">Caricamento mappa…</p>
            </div>
          ) : null}

          <MapContainer
            center={ITALY_CENTER}
            zoom={ITALY_ZOOM}
            minZoom={5}
            maxZoom={16}
            scrollWheelZoom={false}
            zoomControl={false}
            style={{ width: "100%", height: "100%" }}
          >
            <FitBoundsToMarkers locations={locations} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <ZoomControl position="topright" />
            {OFFICES.map((o) => (
              <Marker
                key={o.id}
                position={[o.lat, o.lng]}
                icon={officeIcons[o.id]}
                zIndexOffset={1000}
              >
                <Popup>
                  <div className="font-montserrat min-w-[220px] max-w-[260px]">
                    <p className="text-sm font-bold text-[#0A1F44] leading-snug mb-1">
                      {o.title} Energeide
                    </p>
                    <p className="flex items-start gap-1.5 text-xs text-gray-600 mb-2">
                      <MapPinIcon className="w-3 h-3 text-[#0FB36B] mt-0.5 shrink-0" />
                      <span>
                        {o.line1}
                        <br />
                        {o.line2}
                      </span>
                    </p>
                    {o.note ? (
                      <span className="inline-block text-[11px] font-semibold text-[#0A1F44] bg-[#F4C542]/25 px-2 py-0.5 rounded">
                        {o.note}
                      </span>
                    ) : null}
                  </div>
                </Popup>
              </Marker>
            ))}
            {locations.map((loc) => {
              const previews = getImagesForLocation(projects, loc.id).slice(0, 3);
              return (
                <Marker
                  key={loc.id || `${loc.lat}-${loc.lng}`}
                  position={[loc.lat, loc.lng]}
                  icon={energeideIcon}
                >
                  <Popup>
                    <div className="font-montserrat min-w-[220px] max-w-[260px]">
                      <p className="text-sm font-bold text-[#0A1F44] leading-snug mb-2">
                        {loc.title}
                      </p>
                      {loc.location && (
                        <p className="flex items-center gap-1.5 text-xs text-gray-600 mb-2">
                          <MapPinIcon className="w-3 h-3 text-[#0FB36B]" />
                          {loc.location}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1.5 text-[11px] mb-2">
                        {loc.power ? (
                          <span className="inline-flex items-center gap-1 bg-[#F4C542]/20 text-[#0A1F44] px-2 py-0.5 rounded font-semibold">
                            <Sun className="w-3 h-3" />
                            {loc.power}
                          </span>
                        ) : null}
                        {loc.year ? (
                          <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            <Calendar className="w-3 h-3" />
                            {loc.year}
                          </span>
                        ) : null}
                      </div>
                      {previews.length > 0 ? (
                        <div className="grid grid-cols-3 gap-1 mb-2">
                          {previews.map((src, idx) => (
                            <button
                              key={`${loc.id}-${idx}`}
                              type="button"
                              onClick={() => onSelectProject && onSelectProject(loc.id)}
                              className="relative block aspect-square rounded-md overflow-hidden bg-gray-100 group cursor-zoom-in"
                              aria-label={`Anteprima foto ${idx + 1}`}
                            >
                              <img
                                src={src}
                                alt=""
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              />
                            </button>
                          ))}
                        </div>
                      ) : null}
                      {onSelectProject && loc.id ? (
                        <button
                          type="button"
                          onClick={() => onSelectProject(loc.id)}
                          className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#0A1F44] hover:text-[#0FB36B] transition-colors"
                        >
                          Vedi il progetto
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      ) : null}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>

          {!loading ? <MapLegend count={locations.length} /> : null}

          {!loading && locations.length === 0 ? (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[400] bg-white/95 backdrop-blur-sm rounded-lg shadow-md px-3 py-2 text-xs font-montserrat text-gray-600">
              Nessuna installazione disponibile al momento.
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default ProjectsMap;
